import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight, LuDownload, LuSearch, LuThumbsDown, LuThumbsUp } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import InfoTitulo from '../components/InfoTitulo';
import MonthPicker from '../components/MonthPicker';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import AssistenteMarkdown from '../components/AssistenteMarkdown';
import { FiltroPill, ListRow, PerPage } from '../components/ListKit';
import { useRecorte } from '../hooks/useRecorte';
import {
    AssistenteSistemaService,
    type ConversaDaPergunta,
    type Desfecho,
    type MensagemDaConversa,
    type PaginaPerguntas,
    type PerguntaLinha,
    type ResumoAssistente,
} from '../services/AssistenteSistemaService';
import './AssistenteSistemaPage.css';

/**
 * Assistente — área Sistema (só GB Code).
 *
 * Direção B, "caderno de perguntas": a unidade da tela é a PERGUNTA. Validar o
 * assistente é julgar resposta, e aqui toda pergunta de qualquer empresa está a
 * um clique da resposta e do que ele consultou para responder. O filtro "Sem
 * resposta" é a fila de triagem.
 *
 * docs/designsystem/propostas/admin/fase-3-assistente.html
 */

const COLUNAS = 'minmax(300px, 1fr) 210px 170px 150px 70px 110px';
const POR_PAGINA = [25, 50, 100];

/* Nome, cor do ponto e ordem de cada desfecho. A forma é a mesma para todos
   (ponto + rótulo); só a cor muda — a tela diferencia por cor, nunca por forma. */
const DESFECHOS: Record<Desfecho, { rotulo: string; classe: string }> = {
    respondida: { rotulo: 'Respondida', classe: 'is-ok' },
    sem_dado: { rotulo: 'Sem dado', classe: 'is-sd' },
    fora_do_assunto: { rotulo: 'Fora do assunto', classe: 'is-fa' },
    sem_consulta: { rotulo: 'Sem consulta', classe: 'is-sc' },
    falha: { rotulo: 'Falha técnica', classe: 'is-fl' },
    conversa: { rotulo: 'Conversa', classe: 'is-cv' },
    nao_medido: { rotulo: 'Não medido', classe: 'is-nm' },
    sem_resposta: { rotulo: 'Sem resposta gravada', classe: 'is-fl' },
};

/* O grupo "Sem resposta" vem primeiro no filtro: é por ele que se começa. */
const GRUPO_SEM_RESPOSTA = 'Sem resposta';

const AVALIACOES = { util: 'Útil', nao_util: 'Não útil', sem: 'Sem avaliação' } as const;
type ChaveAvaliacao = keyof typeof AVALIACOES;

const mesCorrente = () => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

const limitesDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    const ultimo = new Date(ano, m, 0).getDate();
    return { de: `${mes}-01`, ate: `${mes}-${String(ultimo).padStart(2, '0')}` };
};

const numero = (v: number) => v.toLocaleString('pt-BR');
const reais = (v: number, casas = 2) =>
    `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}`;
const segundos = (ms: number | null | undefined) =>
    ms == null ? '—' : `${(ms / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
/**
 * A linha de uma pergunta aberta pelo link de Sistema › Sugestões (`?pergunta=<id>`), antes
 * de a conversa chegar: só o id vale. O título e o subtítulo saem da conversa carregada.
 */
const linhaDoLink = (mensagemId: number): PerguntaLinha => ({
    mensagemId, conversaId: 0, pergunta: '', empresaId: 0, empresaNome: '', usuarioNome: '',
    origem: null, conversaApagada: false, criadaEm: '', respostaId: null, desfecho: 'nao_medido',
    desfechoDetalhe: null, consultou: [], duracaoMs: null, avaliacao: null,
});

const quando = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};
const dataCurta = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/** Argumentos do rastro em uma linha: `mes = 2026-09 · parado_ha_dias = 7`. */
const argumentosEmLinha = (a: Record<string, unknown>) => {
    const pares = Object.entries(a ?? {});
    return pares.length === 0 ? 'sem argumentos' : pares.map(([k, v]) => `${k} = ${String(v)}`).join(' · ');
};

const PontoDesfecho = ({ desfecho }: { desfecho: Desfecho }) => {
    const d = DESFECHOS[desfecho] ?? DESFECHOS.nao_medido;
    return <span className={`sia-ds ${d.classe}`}><i />{d.rotulo}</span>;
};

const AssistenteSistemaPage = () => {
    /* O recorte de trabalho não se perde ao abrir uma pergunta e voltar. */
    const [recorte, gravarRecorte] = useRecorte('sia:recorte', {
        mes: mesCorrente(),
        busca: '',
        desfecho: null as string | null,
        empresaId: null as number | null,
        ferramenta: null as string | null,
        avaliacao: null as ChaveAvaliacao | null,
        pagina: 1,
        porPagina: 50,
    });
    const [mes, setMes] = useState(recorte.mes);
    const [busca, setBusca] = useState(recorte.busca);
    const [buscaAplicada, setBuscaAplicada] = useState(recorte.busca);
    const [desfecho, setDesfecho] = useState<string | null>(recorte.desfecho);
    const [empresaId, setEmpresaId] = useState<number | null>(recorte.empresaId);
    const [ferramenta, setFerramenta] = useState<string | null>(recorte.ferramenta);
    const [avaliacao, setAvaliacao] = useState<ChaveAvaliacao | null>(recorte.avaliacao);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);

    useEffect(() => {
        gravarRecorte({ mes, busca, desfecho, empresaId, ferramenta, avaliacao, pagina, porPagina });
    }, [mes, busca, desfecho, empresaId, ferramenta, avaliacao, pagina, porPagina, gravarRecorte]);

    /* A busca vai ao servidor depois de uma pausa na digitação, não a cada tecla. */
    useEffect(() => {
        const t = setTimeout(() => setBuscaAplicada(busca), 300);
        return () => clearTimeout(t);
    }, [busca]);

    /* Mudou o recorte, a página volta para a primeira — senão "página 3 de 1". */
    const [recorteAnterior, setRecorteAnterior] = useState({ mes, buscaAplicada, desfecho, empresaId, ferramenta, avaliacao, porPagina });
    if (recorteAnterior.mes !== mes || recorteAnterior.buscaAplicada !== buscaAplicada || recorteAnterior.desfecho !== desfecho
        || recorteAnterior.empresaId !== empresaId || recorteAnterior.ferramenta !== ferramenta
        || recorteAnterior.avaliacao !== avaliacao || recorteAnterior.porPagina !== porPagina) {
        setRecorteAnterior({ mes, buscaAplicada, desfecho, empresaId, ferramenta, avaliacao, porPagina });
        setPagina(1);
    }

    const [resumo, setResumo] = useState<ResumoAssistente | null>(null);
    const [lista, setLista] = useState<PaginaPerguntas | null>(null);
    const [carregandoResumo, setCarregandoResumo] = useState(true);
    const [carregandoLista, setCarregandoLista] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        const { de, ate } = limitesDoMes(mes);
        setCarregandoResumo(true);
        AssistenteSistemaService.resumo(de, ate)
            .then(r => { if (vivo) setResumo(r); })
            .catch(e => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível carregar.'); })
            .finally(() => { if (vivo) setCarregandoResumo(false); });
        return () => { vivo = false; };
    }, [mes]);

    useEffect(() => {
        let vivo = true;
        const { de, ate } = limitesDoMes(mes);
        setCarregandoLista(true);
        setErro(null);
        AssistenteSistemaService.perguntas({
            de, ate, desfecho, empresaId, ferramenta, avaliacao, busca: buscaAplicada, pagina, tamanho: porPagina,
        })
            .then(l => { if (vivo) setLista(l); })
            .catch(e => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível carregar.'); })
            .finally(() => { if (vivo) setCarregandoLista(false); });
        return () => { vivo = false; };
    }, [mes, desfecho, empresaId, ferramenta, avaliacao, buscaAplicada, pagina, porPagina]);

    /* ─── Opções dos filtros (o FiltroPill trabalha com o rótulo) ─── */
    const opcoesDesfecho = useMemo(() => {
        if (!resumo) return [];
        const itens = resumo.porDesfecho.map(p => ({ valor: DESFECHOS[p.desfecho]?.rotulo ?? p.desfecho, total: p.total }));
        return [{ valor: GRUPO_SEM_RESPOSTA, total: resumo.semResposta }, ...itens];
    }, [resumo]);

    const rotuloDesfecho = desfecho === 'sem_resposta_grupo'
        ? GRUPO_SEM_RESPOSTA
        : desfecho ? DESFECHOS[desfecho as Desfecho]?.rotulo ?? desfecho : null;

    const escolherDesfecho = (rotulo: string | null) => {
        if (!rotulo) return setDesfecho(null);
        if (rotulo === GRUPO_SEM_RESPOSTA) return setDesfecho('sem_resposta_grupo');
        const chave = (Object.keys(DESFECHOS) as Desfecho[]).find(k => DESFECHOS[k].rotulo === rotulo);
        setDesfecho(chave ?? null);
    };

    const opcoesEmpresa = (resumo?.empresasComPergunta ?? []).map(e => ({ valor: e.empresaNome, total: e.perguntas }));
    const rotuloEmpresa = resumo?.empresasComPergunta.find(e => e.empresaId === empresaId)?.empresaNome ?? (empresaId ? `#${empresaId}` : null);

    /* As que nunca rodaram entram na lista com zero: é assim que "a ferramenta
       que ninguém usa" aparece nesta tela. */
    const opcoesFerramenta = (resumo?.ferramentas ?? []).map(f => ({ valor: f.rotulo, total: f.usos }));
    const rotuloFerramenta = resumo?.ferramentas.find(f => f.ferramenta === ferramenta)?.rotulo ?? ferramenta;

    const semAvaliacao = resumo ? Math.max(0, resumo.perguntas - resumo.uteis - resumo.naoUteis) : 0;
    const opcoesAvaliacao = resumo
        ? [
            { valor: AVALIACOES.util, total: resumo.uteis },
            { valor: AVALIACOES.nao_util, total: resumo.naoUteis },
            { valor: AVALIACOES.sem, total: semAvaliacao },
        ]
        : [];

    /* ─── Painel ─── */
    const [aberta, setAberta] = useState<PerguntaLinha | null>(null);
    const [conversa, setConversa] = useState<ConversaDaPergunta | null>(null);
    const [carregandoConversa, setCarregandoConversa] = useState(false);

    /* "Ver conversa" de Sistema › Sugestões chega com ?pergunta=<id> e abre o
       painel direto nela, sem depender do mês nem dos filtros da lista. */
    const [params, setParams] = useSearchParams();
    useEffect(() => {
        const id = Number(params.get('pergunta'));
        if (id > 0) setAberta(linhaDoLink(id));
        // Só na chegada: fechar o painel tira o parâmetro.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const fechar = () => {
        setAberta(null);
        if (params.has('pergunta')) setParams({}, { replace: true });
    };
    const selecionada = conversa?.mensagens.find(m => m.mensagemId === conversa.mensagemSelecionadaId);

    useEffect(() => {
        if (!aberta) { setConversa(null); return; }
        let vivo = true;
        setCarregandoConversa(true);
        AssistenteSistemaService.conversa(aberta.mensagemId)
            .then(c => { if (vivo) setConversa(c); })
            .catch(() => { if (vivo) setConversa(null); })
            .finally(() => { if (vivo) setCarregandoConversa(false); });
        return () => { vivo = false; };
    }, [aberta]);

    const exportar = async () => {
        const { de, ate } = limitesDoMes(mes);
        const tudo = await AssistenteSistemaService.perguntas({
            de, ate, desfecho, empresaId, ferramenta, avaliacao, busca: buscaAplicada, pagina: 1, tamanho: 5000,
        });
        const linhas = [
            ['Quando', 'Empresa', 'Pessoa', 'Pergunta', 'Origem', 'Desfecho', 'O que pediu / causa', 'Consultou', 'Tempo (s)', 'Avaliação', 'Conversa apagada'],
            ...tudo.itens.map(p => [
                new Date(p.criadaEm).toLocaleString('pt-BR'), p.empresaNome, p.usuarioNome, p.pergunta,
                p.origem ?? '', DESFECHOS[p.desfecho]?.rotulo ?? p.desfecho, p.desfechoDetalhe ?? '',
                p.consultou.join(', '), p.duracaoMs == null ? '' : (p.duracaoMs / 1000).toFixed(1).replace('.', ','),
                p.avaliacao === 1 ? 'útil' : p.avaliacao === -1 ? 'não útil' : '', p.conversaApagada ? 'sim' : '',
            ]),
        ];
        // Ponto e vírgula porque o Excel em pt-BR usa vírgula como decimal.
        const csv = linhas.map(l => l.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
        const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `assistente-${mes}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const total = lista?.total ?? 0;
    const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
    const inicio = (pagina - 1) * porPagina;
    const r = resumo;
    const vazio = !carregandoLista && !erro && total === 0;

    return (
        <div className="page-shell lk-screen sia-assistente">
            <PageHeader
                title="Assistente"
                /* Dizer DESDE QUANDO se mede continua a um passo do título: sem
                   isso, "Não medido" em metade da lista parece defeito, e o custo
                   parece o do mês inteiro. */
                titleInfo={r ? (
                    <InfoTitulo rotulo="Como isto é medido">
                        <span className="rl-info-t">Como isto é medido</span>
                        <span className="rl-info-d">
                            <b>Desde quando</b>
                            {r.medindoDesde
                                ? <>Desfecho, consultas e custo são gravados desde <strong>{dataCurta(r.medindoDesde)}</strong>; perguntas anteriores aparecem como <em>Não medido</em>.</>
                                : <>Nenhuma resposta com medição ainda.</>}
                        </span>
                        <span className="rl-info-d">
                            <b>Custo</b>
                            É estimativa: tokens × preço do modelo
                            {r.cotacao ? ` × cotação de R$ ${r.cotacao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : ''}
                            {r.tarifaVigenteDesde ? `, tabela vigente desde ${dataCurta(r.tarifaVigenteDesde)}` : ''}.
                        </span>
                    </InfoTitulo>
                ) : undefined}
                subtitle="Perguntas feitas ao assistente da Home, em todas as empresas"
                actions={
                    <div className="sia-acoes">
                        <MonthPicker value={mes} onChange={setMes} />
                        <button type="button" className="sia-btn" onClick={exportar} disabled={total === 0 || carregandoLista}>
                            <LuDownload size={15} />
                            <span>Exportar</span>
                        </button>
                    </div>
                }
            />

            <div className="page-body">
                {erro && <div className="sia-aviso">{erro}</div>}

                <div className="sia-tiles">
                    <Bloco
                        rotulo="Perguntas"
                        valor={carregandoResumo ? '—' : numero(r?.perguntas ?? 0)}
                        apoio={r ? `${r.empresas} ${r.empresas === 1 ? 'empresa' : 'empresas'} · ${r.pessoas} ${r.pessoas === 1 ? 'pessoa' : 'pessoas'}${r.deSugestao ? ` · ${r.deSugestao} de sugestão` : ''}` : undefined}
                    />
                    <Bloco
                        rotulo="Respondidas"
                        valor={carregandoResumo ? '—' : r?.taxaResposta == null ? '—' : `${r.taxaResposta}%`}
                        apoio={r
                            ? r.medidas === 0
                                ? 'nada medido neste período'
                                : `${r.respondidas} de ${r.medidas - (r.porDesfecho.find(p => p.desfecho === 'conversa')?.total ?? 0)}, sem contar conversa${r.naoUteis ? ` · ${r.naoUteis} não ${r.naoUteis === 1 ? 'útil' : 'úteis'}` : ''}`
                            : undefined}
                    />
                    <Bloco
                        rotulo="Sem resposta"
                        valor={carregandoResumo ? '—' : numero(r?.semResposta ?? 0)}
                        apoio={r ? resumoSemResposta(r) : undefined}
                        alerta={(r?.semResposta ?? 0) > 0}
                    />
                    <Bloco
                        rotulo="Tempo de resposta"
                        valor={carregandoResumo ? '—' : segundos(r?.tempoMedianaMs)}
                        apoio={r?.tempoP90Ms != null ? `mediana · 90% abaixo de ${segundos(r.tempoP90Ms)}` : undefined}
                    />
                    <Bloco
                        rotulo="Custo estimado"
                        valor={carregandoResumo ? '—' : reais(r?.custoEstimado ?? 0)}
                        apoio={r?.custoPorPergunta != null ? `${reais(r.custoPorPergunta, 4)} por resposta · ${numero(r.tokensEntrada + r.tokensSaida)} tokens` : undefined}
                    />
                </div>

                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar na pergunta…" value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>

                    <FiltroPill rotulo="Desfecho" valor={rotuloDesfecho} opcoes={opcoesDesfecho} onChange={escolherDesfecho} />
                    <FiltroPill
                        rotulo="Empresa"
                        valor={rotuloEmpresa}
                        opcoes={opcoesEmpresa}
                        onChange={v => setEmpresaId(v ? resumo?.empresasComPergunta.find(e => e.empresaNome === v)?.empresaId ?? null : null)}
                    />
                    <FiltroPill
                        rotulo="Consultou"
                        valor={rotuloFerramenta}
                        opcoes={opcoesFerramenta}
                        onChange={v => setFerramenta(v ? resumo?.ferramentas.find(f => f.rotulo === v)?.ferramenta ?? null : null)}
                    />
                    <FiltroPill
                        rotulo="Avaliação"
                        valor={avaliacao ? AVALIACOES[avaliacao] : null}
                        opcoes={opcoesAvaliacao}
                        onChange={v => setAvaliacao(v ? (Object.keys(AVALIACOES) as ChaveAvaliacao[]).find(k => AVALIACOES[k] === v) ?? null : null)}
                    />

                    <span className="lk-count">{numero(total)} {total === 1 ? 'pergunta' : 'perguntas'}</span>
                </div>

                {vazio ? (
                    <div className="sia-vazio">
                        <p className="sia-vazio-t">Nenhuma pergunta neste recorte.</p>
                        <p className="sia-vazio-d">Troque o mês ou limpe os filtros.</p>
                    </div>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Pergunta</div>
                                <div className="lk-head">Empresa · pessoa</div>
                                <div className="lk-head">Desfecho</div>
                                <div className="lk-head">Consultou</div>
                                <div className="lk-head sia-r">Tempo</div>
                                <div className="lk-head sia-r">Quando</div>
                            </div>
                            <div className="lk-body">
                                {carregandoLista
                                    ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : (lista?.itens ?? []).map(p => (
                                        <ListRow
                                            key={p.mensagemId}
                                            columns={COLUNAS}
                                            selected={aberta?.mensagemId === p.mensagemId}
                                            onOpen={() => setAberta(p)}
                                            label={`Abrir a pergunta "${p.pergunta}"`}
                                        >
                                            <span className="lk-cell sia-pergunta">
                                                <span className="sia-pergunta-t" title={p.pergunta}>{p.pergunta}</span>
                                                {p.origem === 'sugestao' && <span className="lk-tag">sugestão</span>}
                                                {p.conversaApagada && <span className="lk-tag">apagada</span>}
                                            </span>
                                            <span className="lk-cell sia-mut" title={`${p.empresaNome} · ${p.usuarioNome}`}>{p.empresaNome} · {p.usuarioNome}</span>
                                            <span className="lk-cell sia-desfecho">
                                                <PontoDesfecho desfecho={p.desfecho} />
                                                {p.avaliacao === 1 && <LuThumbsUp size={12} className="sia-aval is-util" aria-label="avaliada como útil" />}
                                                {p.avaliacao === -1 && <LuThumbsDown size={12} className="sia-aval is-nao" aria-label="avaliada como não útil" />}
                                            </span>
                                            {/* Antes da medição não se sabe o que foi consultado: "nada"
                                                seria afirmar uma coisa que o banco não diz. */}
                                            <span className={`lk-cell ${p.consultou.length ? '' : 'sia-ghost'}`}>
                                                {p.consultou.length ? p.consultou.join(', ') : p.desfecho === 'nao_medido' ? '—' : 'nada'}
                                            </span>
                                            <span className="lk-cell sia-r">{segundos(p.duracaoMs)}</span>
                                            <span className="lk-cell sia-r sia-mut">{quando(p.criadaEm)}</span>
                                        </ListRow>
                                    ))}
                            </div>
                        </div>

                        {!carregandoLista && total > 0 && (
                            <div className="lk-foot">
                                <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
                                <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, total)} de {total}</span>
                                <span className="lk-pages">
                                    <button type="button" className="lk-page" aria-label="Página anterior"
                                        disabled={pagina === 1} onClick={() => setPagina(x => Math.max(1, x - 1))}>
                                        <LuChevronLeft size={13} />
                                    </button>
                                    <span className="lk-page is-current">{pagina}</span>
                                    <span className="lk-muted">de {totalPaginas}</span>
                                    <button type="button" className="lk-page" aria-label="Próxima página"
                                        disabled={pagina >= totalPaginas} onClick={() => setPagina(x => Math.min(totalPaginas, x + 1))}>
                                        <LuChevronRight size={13} />
                                    </button>
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <RecordPanel
                open={!!aberta}
                onClose={fechar}
                title={aberta?.pergunta || selecionada?.conteudo || ''}
                subtitle={aberta?.empresaNome
                    ? `${aberta.empresaNome} · ${aberta.usuarioNome} · ${quando(aberta.criadaEm)}${aberta.conversaApagada ? ' · conversa apagada pela pessoa' : ''}`
                    : conversa && selecionada
                        ? `${conversa.empresaNome} · ${conversa.usuarioNome} · ${quando(selecionada.criadaEm)}${conversa.conversaApagada ? ' · conversa apagada pela pessoa' : ''}`
                        : undefined}
                wide
            >
                {carregandoConversa || !conversa
                    ? <PanelSection title="Conversa"><p className="sia-fnt">{carregandoConversa ? 'Carregando a conversa…' : 'Não foi possível abrir a conversa.'}</p></PanelSection>
                    : <PainelConversa conversa={conversa} />}
            </RecordPanel>
        </div>
    );
};

const resumoSemResposta = (r: ResumoAssistente) => {
    const conta = (d: Desfecho) => r.porDesfecho.find(p => p.desfecho === d)?.total ?? 0;
    const partes = [
        [conta('fora_do_assunto'), 'fora do assunto'],
        [conta('sem_dado'), 'sem dado'],
        [conta('sem_consulta'), 'sem consulta'],
        [conta('falha') + conta('sem_resposta'), 'falha'],
    ].filter(([n]) => (n as number) > 0).map(([n, t]) => `${n} ${t}`);
    return partes.length ? partes.join(' · ') : 'nenhuma no período';
};

const Bloco = ({ rotulo, valor, apoio, alerta }: { rotulo: string; valor: string; apoio?: string; alerta?: boolean }) => (
    <div className={`sia-tile ${alerta ? 'is-alerta' : ''}`}>
        <div className="sia-tile-k">{rotulo}</div>
        <div className="sia-tile-v">{valor}</div>
        {apoio && <div className="sia-tile-d">{apoio}</div>}
    </div>
);

/** A conversa em volta da pergunta, com o rastro da resposta dela embaixo. */
const PainelConversa = ({ conversa }: { conversa: ConversaDaPergunta }) => {
    const indice = conversa.mensagens.findIndex(m => m.mensagemId === conversa.mensagemSelecionadaId);
    const resposta = conversa.mensagens.slice(indice + 1).find(m => m.papel === 'model') ?? null;
    const respostaEhDaSelecionada = resposta && !conversa.mensagens
        .slice(indice + 1, conversa.mensagens.indexOf(resposta)).some(m => m.papel === 'user');

    return (
        <>
            <PanelSection title="Conversa">
            <div className="sia-conversa">
                {conversa.mensagens.map(m => (
                    <div
                        key={m.mensagemId}
                        className={[
                            'sia-bolha',
                            m.papel === 'user' ? 'is-user' : 'is-model',
                            m.mensagemId === conversa.mensagemSelecionadaId ? 'is-selecionada' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {m.papel === 'model' ? <AssistenteMarkdown texto={m.conteudo} /> : m.conteudo}
                        {m.papel === 'model' && (
                            <div className="sia-bolha-meta">
                                <PontoDesfecho desfecho={m.desfecho ?? 'nao_medido'} />
                                <span>{m.rastro.length ? `${m.rastro.length} ${m.rastro.length === 1 ? 'consulta' : 'consultas'}` : 'nenhuma consulta'}</span>
                                {m.duracaoMs != null && <span>{segundos(m.duracaoMs)}</span>}
                                {m.avaliacao === 1 && <span className="sia-aval is-util"><LuThumbsUp size={12} /> útil</span>}
                                {m.avaliacao === -1 && <span className="sia-aval is-nao"><LuThumbsDown size={12} /> não útil</span>}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            </PanelSection>

            <PanelSection title="Rastro desta resposta">
                {resposta && respostaEhDaSelecionada
                    ? <Rastro resposta={resposta} />
                    : <p className="sia-fnt">A resposta desta pergunta não foi gravada.</p>}
            </PanelSection>
        </>
    );
};

const Rastro = ({ resposta }: { resposta: MensagemDaConversa }) => {
    if (!resposta.desfecho || resposta.desfecho === 'nao_medido') {
        return <p className="sia-fnt">Resposta anterior à medição: só o texto e as fontes foram gravados.</p>;
    }

    return (
        <div className="sia-kv">
            <span className="k">Desfecho</span>
            <span>
                <PontoDesfecho desfecho={resposta.desfecho} />
                {resposta.desfechoDetalhe && <span className="sia-detalhe"> — {resposta.desfechoDetalhe}</span>}
            </span>

            <span className="k">Consultas</span>
            <span className="sia-rastro">
                {resposta.rastro.length === 0
                    ? <span className="sia-fnt">nenhuma</span>
                    : resposta.rastro.map((c, i) => (
                        <span key={i} className="sia-rastro-l">
                            <code className="sia-chip">{c.ferramenta}</code>
                            <span className="sia-fnt">
                                {argumentosEmLinha(c.argumentos)} · {c.ok ? 'ok' : <span className="sia-erro">recusou: {c.erro}</span>} · {c.ms} ms
                            </span>
                        </span>
                    ))}
            </span>

            <span className="k">Idas ao modelo</span>
            <span>{resposta.rodadas ?? '—'}</span>

            <span className="k">Tokens</span>
            <span>{numero(resposta.tokensEntrada ?? 0)} entrada · {numero(resposta.tokensSaida ?? 0)} saída</span>

            <span className="k">Modelo</span>
            <span>{resposta.modelo ? <code className="sia-chip">{resposta.modelo}</code> : '—'}</span>

            <span className="k">Custo estimado</span>
            <span>{resposta.custoEstimado != null ? reais(resposta.custoEstimado, 4) : '—'}</span>

            <span className="k">Tempo</span>
            <span>{segundos(resposta.duracaoMs)}</span>
        </div>
    );
};

export default AssistenteSistemaPage;
